import { useEffect, useState } from "react";
import { api } from "../api/client";
import { useErrorHandler } from "../auth/AuthContext";
import { formatCents } from "../lib/money";

const PIE_RADIUS = 42;
const PIE_CIRCUMFERENCE = 2 * Math.PI * PIE_RADIUS;

/**
 * A donut showing the Spend/Save division.
 *
 * A pie rather than a horizontal bar because the bar fought the slider: the
 * slider's value is Save, but Spend was drawn on the left, so dragging the
 * thumb left grew the left-hand segment — the indicator appeared to move
 * opposite the thumb. A circle has no left or right to contradict, and the two
 * wedges visibly trade against each other.
 */
function SplitPie({ savePercent }: { savePercent: number }) {
  const spendPercent = 100 - savePercent;
  const spendLength = (spendPercent / 100) * PIE_CIRCUMFERENCE;
  const saveLength = (savePercent / 100) * PIE_CIRCUMFERENCE;

  return (
    <div className="split-pie">
      <svg viewBox="0 0 100 100" role="img" aria-hidden="true">
        {/* Spend starts at twelve o'clock and runs clockwise; Save picks up
            where it ends, so growing Save is seen taking from Spend. */}
        <circle
          cx="50"
          cy="50"
          r={PIE_RADIUS}
          fill="none"
          stroke="var(--c-spend)"
          strokeWidth="16"
          strokeDasharray={`${spendLength} ${PIE_CIRCUMFERENCE}`}
          transform="rotate(-90 50 50)"
        />
        <circle
          cx="50"
          cy="50"
          r={PIE_RADIUS}
          fill="none"
          stroke="var(--c-save)"
          strokeWidth="16"
          strokeDasharray={`${saveLength} ${PIE_CIRCUMFERENCE}`}
          strokeDashoffset={-spendLength}
          transform="rotate(-90 50 50)"
        />
      </svg>
    </div>
  );
}

/**
 * The child's control over where their allowance lands.
 *
 * A single slider rather than two inputs: Save takes its percentage and Spend
 * takes the rest, so a split that does not add up to 100 cannot be expressed.
 * The preview names real money against the child's actual allowance, because
 * "30%" means much less to a kid than "$6 of your $20".
 */
export function SplitChooser({
  childId,
  savePercent,
  allowanceCents,
  onSaved,
}: {
  childId: string;
  savePercent: number;
  allowanceCents: number | null;
  onSaved: (savePercent: number) => void;
}) {
  const toMessage = useErrorHandler();

  const [value, setValue] = useState(savePercent);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [justSaved, setJustSaved] = useState(false);

  // Follow the server if it changes underneath us (another device, a parent).
  useEffect(() => {
    setValue(savePercent);
  }, [savePercent]);

  useEffect(() => {
    if (!justSaved) return;
    const timer = window.setTimeout(() => setJustSaved(false), 2000);
    return () => window.clearTimeout(timer);
  }, [justSaved]);

  const dirty = value !== savePercent;

  async function save() {
    setSaving(true);
    setError("");
    try {
      await api.setSplit(childId, value);
      onSaved(value);
      setJustSaved(true);
    } catch (err) {
      setError(toMessage(err));
      setValue(savePercent);
    } finally {
      setSaving(false);
    }
  }

  const saveCents =
    allowanceCents === null
      ? null
      : Math.floor((allowanceCents * value) / 100);
  const spendCents =
    allowanceCents === null || saveCents === null
      ? null
      : allowanceCents - saveCents;

  return (
    <div className="split-chooser">
      <div className="split-chooser__head">
        <h2>Where your allowance goes</h2>
        <p className="muted">
          You choose how to divide it. This changes your next allowance — money
          you already have stays where it is.
        </p>
      </div>

      <SplitPie savePercent={value} />

      <div className="split-chooser__labels">
        <span className="split-chooser__label">
          <span className="split-chooser__swatch split-chooser__swatch--spend" />
          Spend {100 - value}%
          {spendCents !== null ? (
            <span className="faint"> · {formatCents(spendCents)}</span>
          ) : null}
        </span>
        <span className="split-chooser__label">
          <span className="split-chooser__swatch split-chooser__swatch--save" />
          Save {value}%
          {saveCents !== null ? (
            <span className="faint"> · {formatCents(saveCents)}</span>
          ) : null}
        </span>
      </div>

      <label className="split-chooser__slider">
        <span className="sr-only">Percent of allowance to save</span>
        <input
          type="range"
          min={0}
          max={100}
          step={5}
          value={value}
          onChange={(e) => setValue(Number(e.target.value))}
          disabled={saving}
          aria-valuetext={`${value}% Save, ${100 - value}% Spend`}
        />
        <span className="split-chooser__ends" aria-hidden="true">
          <span>All Spend</span>
          <span>All Save</span>
        </span>
      </label>

      {error ? (
        <p className="alert" role="alert">
          {error}
        </p>
      ) : null}

      <div className="row row--between">
        <span className="muted" aria-live="polite">
          {justSaved ? "Saved!" : dirty ? "Not saved yet" : ""}
        </span>
        <button
          type="button"
          className="btn btn--primary btn--small"
          onClick={save}
          disabled={!dirty || saving}
        >
          {saving ? "Saving…" : "Save this"}
        </button>
      </div>
    </div>
  );
}
